import type {
  DefaultOptions,
} from '@tanstack/react-query';
import {
  isServer,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query';

import { toast } from '@/components/ui/use-toast';
import { STALETIME_DEFAULT } from '@/constants';
import { errorToString } from '@/utils';

export function makeQueryClient() {
  const defaultOptions: DefaultOptions = {
    queries: {
      // With SSR, we usually want to set some default staleTime
      // above 0 to avoid refetching immediately on the client
      staleTime: STALETIME_DEFAULT,
      refetchOnWindowFocus: true,
      retry: false,
      gcTime: 300_000, // 5 min
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Retry after 1 second, then 2, 4, 8, 16, 32, 64 seconds, etc...
    },
  };
  if (isServer) {
    return new QueryClient({ defaultOptions });
  }

  return new QueryClient({
    defaultOptions,
    queryCache: new QueryCache({
      onError: (err) => {
        toast({
          variant: 'destructive',
          title: 'Něco se nepovedlo',
          description: errorToString(err),
        });
      },
    }),
  });
}

let browserQueryClient: QueryClient | undefined;

export function getQueryClient() {
  if (isServer) {
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
    }
    return browserQueryClient;
  }
}
